import React, { useEffect, useState } from "react";
import Image from "./Image";
import googleImg from "../../assets/images/google.svg";
import { useGoogleLogin } from "@react-oauth/google";
import { apiRequest } from "../../util/apiUtils";
import { useDispatch } from "react-redux";
import { setProfile } from "../../redux/slice/profileSlice";
import { setSession } from "../../redux/slice/authSlice";
import { useLocation, useNavigate } from "react-router-dom";
import Modal from "./Modal";
import Select from "../form/select/Select";
import { t } from "i18next";
import Button from "../form/Button";
import { useMutation } from "@tanstack/react-query";

const GoogleLoginButton = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [isPending, setIsPending] = useState(false);
  const [roleProvider, setRoleProvider] = useState({
    isOpen: false,
    role: "user",
    user: null,
  });

  const {
    mutate: socialLogin,
    isPending: isLoginPending,
    error,
  } = useMutation({
    mutationFn: async ({ userInfo, role }) => {
      const resp = await apiRequest("post", "social-login", {
        body: {
          loginData: {
            socialLoginId: userInfo?.sub,
            firstName: userInfo?.name,
            lastName: null,
            image: userInfo?.picture,
            email: userInfo?.email,
            platform: "web",
            socialLoginType: "google",
            email_verified: userInfo?.email_verified,
          },
          ...(role && { role }),
        },
      });

      return resp;
    },
    onSuccess: async (resp) => {
      if (resp?.data?.status === 200) {
        const { accessToken, refreshToken } = resp?.data || {};
        localStorage.setItem("auth_token", accessToken);
        localStorage.setItem("refreshToken", refreshToken);

        const verify = await apiRequest(
          "post",
          `verify-token`,
          {
            body: {},
          },
          "application/json",
          () => {},
          {
            Authorization: "Bearer " + accessToken,
          }
        );

        if (verify?.data?.status === 200) {
          const user = verify?.data?.data || {};

          dispatch(
            setProfile({
              ...user,
              loading: false,
              access_token: accessToken,
            })
          );
          dispatch(
            setSession({
              user: { ...user, access_token: accessToken },
              status: "authorized",
              error: null,
            })
          );

          const params = new URLSearchParams(location?.search);
          const callback = params.get("callback");

          if (callback) {
            navigate(callback);
          } else {
            window.location.href = window.location.origin + "/en/profile";
          }
          setRoleProvider({
            isOpen: false,
            role: "user",
            user: null,
          });
        }
      } else {
        if (resp?.data?.data?.role === false) {
          setRoleProvider((pre) => ({
            ...pre,
            isOpen: true,
          }));
        }
      }
    },
  });

  const login = useGoogleLogin({
    onSuccess: async (res) => {
      if (res?.access_token) {
        try {
          const userInfo = await fetch(
            "https://www.googleapis.com/oauth2/v3/userinfo",
            {
              headers: {
                Authorization: `Bearer ${res.access_token}`,
              },
            }
          ).then((res) => res.json());

          setRoleProvider((pre) => ({ ...pre, user: userInfo }));

          if (Object.values(userInfo)?.length > 0) {
            socialLogin({ userInfo });
          }
          setIsPending(false);
        } catch (err) {
          setIsPending(false);
        }
      }
      setIsPending(false);
    },
  });

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setIsPending(true);
          login();
        }}
        className="flex items-center gap-2 justify-center whitespace-nowrap border border-deep-purple rounded-full py-2 px-2 capitalize text-sm font-medium h-12 hover:opacity-70 transition-all duration-300"
      >
        {isPending ? (
          <i className="fa-duotone fa-solid fa-spinner-third animate-spin text-2xl"></i>
        ) : (
          <Image src={googleImg} alt={"google"} classes={"w-6 h-6"} />
        )}
        Login with Google
      </button>
      <Modal
        isOpen={roleProvider?.isOpen}
        setIsOpen={() => {
          setRoleProvider({
            isOpen: false,
            role: null,
            user: null,
          });
        }}
        size="sm"
        modaltitleClasses={"!text-slate-200 text-[15px]"}
        headerClass={"!bg-black !rounded-t-lg !text-white"}
        childrenClasses={"!rounded-b-lg"}
        titleClass="!text-white"
      >
        <div className="relative">
          <Select
            label={`${t("I want to")}...`}
            placeholder={"Select role"}
            labelClasses="!text-base capitalize"
            dropdownButtonClass={"rounded-md !h-14"}
            onChange={(val) => {
              setRoleProvider((pre) => ({ ...pre, role: val }));
            }}
            selectedValue={roleProvider?.role}
            dropdownData={[
              { value: "user", name: "Book events" },
              { value: "organization", name: "Host events" },
            ]}
            placements={"top-start"}
          />
        </div>
        <div className="py-5 flex justify-end">
          <Button
            buttonClasses={"h-10"}
            buttonLabel={"Save"}
            buttonFunction={() => {
              socialLogin({
                userInfo: roleProvider?.user,
                role: roleProvider?.role,
              });
            }}
            {...(isLoginPending && { isDisable: true, loading: true })}
          />
        </div>
      </Modal>
    </>
  );
};

export default GoogleLoginButton;
